import { createRef, Component } from 'react'
import * as styles from 'components/features/socialFeed/FeedAvatarWithInfo/FeedAvatarWithInfo.styl'
import { FeedUserUid, MatchingPeergroupFilled } from 'interfaces/feed'
import * as debounce from 'lodash/debounce'
import { FeedUserContextInfo } from 'components/features/socialFeed/FeedUserContextInfo/FeedUserContextInfo'
import withMobile, { WithMobileProps } from 'hocs/withMobile/withMobile'
import Touchable from 'components/utils/Touchable'
import { OuterEventTrackerContext, OuterEventTrackerContextType } from 'components/utils/OuterEventTracker'
import { OverlayMobile } from 'components/features/pfm/Overlay/OverlayMobile'
import * as clamp from 'lodash/clamp'
import { UserAvatar } from 'components/features/socialFeed/Avatar/UserAvatar'

type FeedAvatarWithInfoProps = WithMobileProps & {
  userUid: FeedUserUid
  username: string
  peergroup: MatchingPeergroupFilled
  mode?: 'post' | 'comment' | 'page'
}

type FeedAvatarWithInfoState = {
  isContextShown: boolean
  popupOffset: number
}

const STICKY_HEADER_OFFSET = 24
const MAX_POPUP_PADDING = 42
const ARROW_HEIGHT = 24

class FeedAvatarWithInfoComponent extends Component<FeedAvatarWithInfoProps, FeedAvatarWithInfoState> {
  static contextType = OuterEventTrackerContext

  state: FeedAvatarWithInfoState = {
    isContextShown: false,
    popupOffset: MAX_POPUP_PADDING,
  }

  context: OuterEventTrackerContextType

  private containerRef = createRef<HTMLDivElement>()
  private popupRef = createRef<HTMLDivElement>()

  render () {
    const {
      userUid, username,
      peergroup,
      isMobile,
      mode,
    } = this.props
    const { isContextShown, popupOffset } = this.state

    const dislaimerText = 'At the time of this ' + (mode === 'post' ? 'post' : 'comment')

    const userContextInfoChild = (
      <FeedUserContextInfo
        username={ username }
        userUid={ userUid }
        peergroup={ peergroup }
        disclaimer={ mode !== 'page' ? dislaimerText : undefined }
      />
    )

    return (
      <div
        className={ styles.container }
        onMouseEnter={ this.showPopupInfo }
        onMouseLeave={ this.handleMouseLeave }
        ref={ this.containerRef }
      >
        <Touchable onTap={ this.showPopupInfo }>
          <div className={ styles.avatar }>
            <UserAvatar
              userUid={ userUid }
              matchRatio={ peergroup.match.ratio }
            />
          </div>
        </Touchable>
        { isContextShown && !isMobile && (
          <div className={ styles.popup }>
            <div className={ styles.arrow } />
            <div
              className={ styles.popupBody }
              ref={ this.popupRef }
              style={{
                top: -popupOffset,
              }}
            >
              { userContextInfoChild }
            </div>
          </div>
        )}
        { isContextShown && isMobile && (
          <OverlayMobile onClose={ this.hideContextInfo }>
            { userContextInfoChild }
          </OverlayMobile>
        )}
      </div>
    )
  }

  componentDidMount () {
    this.context.addOuterEventListener('tap', this.handleOuterTapDebounced, this.containerRef.current)
  }

  componentDidUpdate (_prevProps: FeedAvatarWithInfoProps, prevState: FeedAvatarWithInfoState) {
    if (this.state.isContextShown && !prevState.isContextShown) {
      this.alignPopupToViewport()
    }
  }

  componentWillUnmount () {
    this.context.removeOuterEventListener('tap', this.handleOuterTapDebounced)
    this.handleMouseLeave.cancel()
    this.handleOuterTapDebounced.cancel()
  }

  private showPopupInfo = () => {
    this.handleMouseLeave.cancel()

    this.setState({
      isContextShown: true,
    })
  }

  private alignPopupToViewport () {
    const rootNode = this.containerRef.current
    const popupNode = this.popupRef.current
    if (!rootNode || !popupNode) {
      return
    }

    const rootRect = rootNode.getBoundingClientRect()
    const popupRect = popupNode.getBoundingClientRect()
    const windowHeight = window.innerHeight

    const mostTopPosition = MAX_POPUP_PADDING
    const mostBottomPosition = popupRect.height - MAX_POPUP_PADDING - ARROW_HEIGHT

    const optimalPopupViewportTop = Math.max(0, Math.round((windowHeight - popupRect.height) / 2))
    const optimalPopupTop = Math.round(rootRect.top - optimalPopupViewportTop)

    this.setState({
      popupOffset: clamp(optimalPopupTop, mostTopPosition, mostBottomPosition) - STICKY_HEADER_OFFSET,
    })
  }

  private hideContextInfo = () => {
    this.setState({
      isContextShown: false,
      popupOffset: MAX_POPUP_PADDING,
    })
  }

  private handleOuterTap = () => {
    const { isMobile } = this.props
    if (isMobile) {
      // no handle outer tap on mobile since it rendered thru portal
      return
    }

    this.hideContextInfo()
  }

  private handleMouseLeave = debounce(this.hideContextInfo, 300)

  // we have to debounce outer tap handler because of tap event closes overlay before click handler invokes
  // it leads to click hit to anything under close button area and it's side bar menu button
  private handleOuterTapDebounced = debounce(this.handleOuterTap, 100)
}

export const FeedAvatarWithInfo = withMobile(FeedAvatarWithInfoComponent)
